import axios, { AxiosResponse } from 'axios';
import {
  GetAllChannelsRequest,
  GetAllChannelsResponse,
  GetUsersEventsResponse,
  GetPopularChannelsResponse,
  GetPopularChannelEventsResponse,
  GetEventsWithCertificatesResponse,
  GetEventsResponse,
  GetAllEventsResponse,
  GetEventDetailsResponse,
  GetEventSoldTicketsResponse,
  GetEventMyTicketsResponse,
  GetMyAllTicketsResponse,
  CreateEventResponse,
  UpdateEventResponse,
  RemoveEventResponse,
  AddSpeakerResponse,
  UpdateSpeakerResponse,
  RemoveSpeakerResponse,
  AddSponsorResponse,
  UpdateSponsorResponse,
  RemoveSponsorResponse,
  AddTicketToEventResponse,
  UpdateTicketToEventResponse,
  RemoveTicketFromEventResponse,
  CreateSoldTicketResponse,
  CommentOnEventResponse,
  RemoveChannelEventCommentRequest,
  RemoveChannelEventCommentResponse,
  UseTicketResponse,
  ExportToExcelResponse,
  CreateEventVisitResponse,
  GetMyChannelsResponse,
  GetChannelRequest,
  GetChannelResponse,
  JoinToChannelResponse,
  LeaveChannelResponse,
  GetChannelUserRequestsRequest,
  GetChannelUserRequestsResponse,
  GetChannelUsersRequest,
  GetChannelUsersResponse,
  AssignAdminToChannelRequest,
  AssignAdminToChannelResponse,
  RevokeAdminFromChannelRequest,
  RevokeAdminFromChannelResponse,
  AcceptUserToPrivateChannelRequest,
  AcceptUserToPrivateChannelResponse,
  RejectUserToPrivateChannelRequest,
  RejectUserToPrivateChannelResponse,
  GetSavedChannelEventsResponse,
  SaveChannelEventResponse,
  RemoveSavedChannelEventResponse,
  CreateChannelRequest,
  CreateChannelResponse,
  UpdateChannelRequest,
  UpdateChannelResponse,
  DeleteChannelRequest,
  DeleteChannelResponse,
  GetPaymentBalanceResponse,
  WithdrawBalanceResponse,
  SearchChannelRequest,
  SearchChannelResponse,
  SearchEventRequest,
  SearchEventResponse,
  UnifiedSearchRequest,
  UnifiedSearchResponse,
  CreateTogatherSessionRequest,
  CreateTogatherSessionResponse,
  SetUserOnlineInTogatherRequest,
  SetUserOnlineInTogatherResponse,
  ChannelUser
} from '../types/service.types';
import {
  GetUserNotificationsResponse,
  SetUserNotificationsReadRequest,
  SetUserNotificationsReadResponse
} from '../types/notification.types';
import {
  InitializePaymentRequest,
  InitializePaymentResponseType,
  ListenPaymentRequest,
  ListenPaymentResponseType
} from '../types/service.types';
import {
  GetTokenRequest,
  GetTokenResponse,
  UserRegisterResponse,
  UserLoginRequest,
  UserLoginResponse,
  SendOTPRequest,
  SendOTPResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  GetUserAuthorizationsRequest,
  GetUserAuthorizationsResponse
} from '../types/auth.types';
import { CoverFile, TicketPayload, SocialMediaLinks, EventSettings } from '../types';
import {
  GetAnyUserInfosRequest,
  GetAnyUserInfosResponse,
  FollowUserRequest,
  FollowUserResponse,
  UnfollowUserRequest,
  UnfollowUserResponse,
  RejectFollowerRequest,
  RejectFollowerResponse,
  AmIFollowingUserRequest,
  AmIFollowingUserResponse,
  GetMyFollowersResponse,
  GetMyFollowingsResponse,
  UpdateUserDataRequest,
  UpdateUserDataResponse
} from '../types/profile.types';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL;
const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_URL;
/**
 * Helper function to wrap service calls with logging and abort controller support
 */
async function withLogging<T>(
  serviceName: string,
  method: string,
  url: string,
  requestFn: (signal?: AbortSignal) => Promise<AxiosResponse<T>>,
  requestData?: any,
  abortController?: AbortController
): Promise<AxiosResponse<T>> {
  // Check if logging out before making request
  if (isLoggingOut()) {
    throw new Error('Request cancelled: Logout in progress');
  }

  // Create abort controller if not provided
  const controller = abortController || new AbortController();
  if (!abortController) {
    registerAbortController(controller);
  }

  try {
    const response = await requestFn(controller.signal);
    return response;
  } catch (error: any) {
    // Ignore abort errors (they're expected during logout)
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  } finally {
    // Unregister controller if we created it
    if (!abortController) {
      unregisterAbortController(controller);
    }
  }
}

const Services = {
  // Authentication Services
  GetToken(
    key: string,
    secret: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetTokenResponse>> {
    const url = `${API_URL}get-token`;
    const requestData: GetTokenRequest = { key, secret };

    return withLogging(
      'GetToken',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      { key: '[REDACTED]', secret: '[REDACTED]' }, // Don't log sensitive data
      abortController
    );
  },

  UserRegister(
    token: string,
    name: string,
    lastname: string,
    email: string,
    password: string
  ): Promise<AxiosResponse<UserRegisterResponse>> {
    const url = `${API_URL}flow/user-registration`;
    const requestData = { name, lastname, email, password, source: 'togather' };

    return withLogging(
      'UserRegister',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      { ...requestData, password: '[REDACTED]' } // Don't log password
    );
  },

  UserLogin(
    token: string,
    credential: string,
    password: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<UserLoginResponse>> {
    const url = `${API_URL}flow/user-login`;
    const requestData: UserLoginRequest = { credential, password };

    return withLogging(
      'UserLogin',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      { credential, password: '[REDACTED]' }, // Don't log password
      abortController
    );
  },

  SendOTPForResetPassword(
    token: string,
    otp: string,
    email: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<SendOTPResponse>> {
    const url = `${API_URL}flow/send-otp-for-change-users-password`;
    const requestData: SendOTPRequest = { otp, email };

    return withLogging(
      'SendOTPForResetPassword',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  ResetPassword(
    token: string,
    otp: string,
    email: string,
    password: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<ResetPasswordResponse>> {
    const url = `${API_URL}flow/change-user-password-with-otp-and-email`;
    const requestData: ResetPasswordRequest = { otp, email, password };

    return withLogging(
      'ResetPassword',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      { ...requestData, password: '[REDACTED]' }, // Don't log password
      abortController
    );
  },

  GetUserAuthorizations(
    token: string,
    userID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetUserAuthorizationsResponse>> {
    const url = `${API_URL}flow/get-users-authorizations-by-user-id`;
    const requestData: GetUserAuthorizationsRequest = { userID };

    return withLogging(
      'GetUserAuthorizations',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  CreatePageView(
    token: string,
    page: string
  ): Promise<AxiosResponse<any>> {
    const url = `${API_URL}flow/create-page-view`;
    const requestData = { page };

    return withLogging(
      'CreatePageView',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetUsersEvents(
    token: string,
    userID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetUsersEventsResponse>> {
    const url = `${API_URL}flow/social-hive-get-users-registered-events`;
    const requestData = { userID };

    return withLogging(
      'SocialHiveGetUsersEvents',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetAllChannels(
    token: string,
    start: number,
    limit: number,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetAllChannelsResponse>> {
    const url = `${API_URL}flow/social-hive-get-all-channels`;
    const requestData: GetAllChannelsRequest = { start, limit };

    return withLogging(
      'SocialHiveGetAllChannels',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetPopularChannels(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetPopularChannelsResponse>> {
    const url = `${API_URL}flow/social-hive-get-popular-channels`;

    return withLogging(
      'SocialHiveGetPopularChannels',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SocialHiveGetPopularChannelEvents(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetPopularChannelEventsResponse>> {
    const url = `${API_URL}flow/social-hive-get-popular-channel-events`;

    return withLogging(
      'SocialHiveGetPopularChannelEvents',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SocialHiveGetEventsWithCertificates(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetEventsWithCertificatesResponse>> {
    const url = `${API_URL}flow/social-hive-get-events-with-certificates`;

    return withLogging(
      'SocialHiveGetEventsWithCertificates',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SocialHiveGetClosestDatesChannelEvents(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetPopularChannelEventsResponse>> {
    const url = `${API_URL}flow/social-hive-get-closest-dates-channel-events`;

    return withLogging(
      'SocialHiveGetClosestDatesChannelEvents',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SocialHiveGetEvents(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetEventsResponse>> {
    const url = `${API_URL}flow/social-hive-get-channel-events`;
    const requestData = { channelID };

    return withLogging(
      'SocialHiveGetEvents',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetAllEvents(
    token: string
  ): Promise<AxiosResponse<GetAllEventsResponse>> {
    const url = `${API_URL}flow/social-hive-get-channel-events`;

    return withLogging(
      'SocialHiveGetAllEvents',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      })
    );
  },

  SocialHiveGetEventDetails(
    token: string,
    eventID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetEventDetailsResponse>> {
    const url = `${API_URL}flow/social-hive-get-channel-event`;
    const requestData = { eventID };

    return withLogging(
      'SocialHiveGetEventDetails',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetEventSoldTickets(
    token: string,
    eventID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetEventSoldTicketsResponse>> {
    const url = `${API_URL}flow/social-hive-get-events-sold-tickets`;
    const requestData = { eventID };

    return withLogging(
      'SocialHiveGetEventSoldTickets',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetEventMyTickets(
    token: string,
    eventID: string,
    userID: string
  ): Promise<AxiosResponse<GetEventMyTicketsResponse>> {
    const url = `${API_URL}flow/social-hive-get-events-sold-tickets`;
    const requestData = { eventID, userID };

    return withLogging(
      'SocialHiveGetEventMyTickets',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveGetMyAllTickets(
    token: string,
    userID: string
  ): Promise<AxiosResponse<GetMyAllTicketsResponse>> {
    const url = `${API_URL}flow/social-hive-get-events-sold-tickets`;
    const requestData = { userID };

    return withLogging(
      'SocialHiveGetMyAllTickets',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveCreateEvent(
    token: string,
    isFree: number,
    channelID: string,
    title: string,
    description: string,
    cover: CoverFile | null,
    eventStartDateTime: string,
    eventEndDateTime: string,
    isOnline: "0" | "1",
    website: string,
    locationAddress: string,
    locationLatitude: string,
    locationLongitude: string,
    organizedBy: string,
    locationAddressDefinition: string,
    hasTickets: "0" | "1",
    tickets: TicketPayload[],
    ticketSaleStartDateTime: string | null,
    contactEmail: string,
    ticketSaleNotificationEmail: string,
    customerNotificationMailTemplate: string,
    contactPhone: string,
    socialMediaLinks: SocialMediaLinks,
    usersCanSendMessage: boolean,
    hasCertificate: boolean,
    isPublic: "0" | "1" | "3",
    settings: EventSettings,
    city?: string
  ): Promise<AxiosResponse<CreateEventResponse>> {
    const url = `${API_URL}flow/social-hive-create-event`;
    const requestData: any = {
      isFree,
      channelID,
      title,
      description,
      cover,
      eventStartDateTime,
      eventEndDateTime,
      isOnline: isOnline === "1",
      website,
      locationAddress,
      locationLatitude,
      locationLongitude,
      locationAddressDefinition,
      organizedBy,
      hasTickets: hasTickets === "1",
      tickets,
      ticketSaleStartDateTime,
      contactEmail,
      ticketSaleNotificationEmail,
      customerNotificationMailTemplate,
      contactPhone,
      socialMediaLinks,
      usersCanSendMessage: false,
      hasCertificate,
      isPublic: Number(isPublic),
      status: 1,
      settings,
      city: city || null
    };

    return withLogging(
      'SocialHiveCreateEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveUpdateEvent(
    token: string,
    payload: any
  ): Promise<AxiosResponse<UpdateEventResponse>> {
    const url = `${API_URL}flow/social-hive-update-event`;

    return withLogging(
      'SocialHiveUpdateEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: payload,
      }),
      payload
    );
  },

  SocialHiveRemoveEvent(
    token: string,
    eventID: string
  ): Promise<AxiosResponse<RemoveEventResponse>> {
    const url = `${API_URL}flow/social-hive-remove-event`;
    const requestData = { eventID };

    return withLogging(
      'SocialHiveRemoveEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveSendEventFirstIntroductionEmail(
    token: string,
    eventID: string
  ): Promise<AxiosResponse<{ status: boolean; message?: string }>> {
    const url = `${API_URL}flow/social-hive-send-event-first-introduction-email`;
    const requestData = { eventID };

    return withLogging(
      'SocialHiveSendEventFirstIntroductionEmail',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveAddSpeaker(
    token: string,
    channelID: string,
    eventID: string,
    name: string,
    lastname: string,
    speakingAboutTitle: string,
    speakingAbout: string,
    about: string,
    image: CoverFile | string | null,
    linkedin: string | null
  ): Promise<AxiosResponse<AddSpeakerResponse>> {
    const url = `${API_URL}flow/social-hive-add-speaker-to-event`;
    const requestData = {
      channelID,
      eventID,
      name,
      lastname,
      speakingAboutTitle,
      speakingAbout,
      about,
      image,
      socialMediaLinks: linkedin ? { linkedin } : undefined,
    };

    return withLogging(
      'SocialHiveAddSpeaker',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveUpdateSpeaker(
    token: string,
    channelID: string,
    eventID: string,
    speakerID: string,
    name: string,
    lastname: string,
    speakingAboutTitle: string,
    speakingAbout: string,
    about: string,
    image: CoverFile | string | null,
    socialMediaLinks?: {
      linkedin?: string;
      instagram?: string;
      x?: string;
    },
    sessionStartDateTime?: string,
    sessionEndDateTime?: string
  ): Promise<AxiosResponse<UpdateSpeakerResponse>> {
    const url = `${API_URL}flow/social-hive-update-event-speaker`;
    const requestData = {
      channelID,
      eventID,
      speakerID,
      name,
      lastname,
      speakingAboutTitle,
      speakingAbout,
      about,
      image,
      socialMediaLinks,
      sessionStartDateTime,
      sessionEndDateTime,
    };

    return withLogging(
      'SocialHiveUpdateSpeaker',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveRemoveSpeaker(
    token: string,
    eventID: string,
    speakerID: string
  ): Promise<AxiosResponse<RemoveSpeakerResponse>> {
    const url = `${API_URL}flow/social-hive-remove-event-speaker`;
    const requestData = { eventID, speakerID };

    return withLogging(
      'SocialHiveRemoveSpeaker',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveAddSponsor(
    token: string,
    channelID: string,
    eventID: string,
    companyTitle: string,
    companyAbout: string,
    sponsorshipType: string,
    sponsorshipAbout: string,
    image: CoverFile | string | null,
    website: string
  ): Promise<AxiosResponse<AddSponsorResponse>> {
    const url = `${API_URL}flow/social-hive-add-sponsor-to-event`;
    const requestData = {
      channelID,
      eventID,
      companyTitle,
      companyAbout,
      sponsorshipType,
      sponsorshipAbout,
      image,
      website,
    };

    return withLogging(
      'SocialHiveAddSponsor',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveUpdateSponsor(
    token: string,
    channelID: string,
    eventID: string,
    sponsorID: string,
    companyTitle: string,
    sponsorshipType: string,
    companyAbout?: string,
    sponsorshipAbout?: string,
    image?: CoverFile | string | null,
    website?: string
  ): Promise<AxiosResponse<UpdateSponsorResponse>> {
    const url = `${API_URL}flow/social-hive-update-event-sponsor`;
    const requestData = {
      channelID,
      eventID,
      sponsorID,
      companyTitle,
      companyAbout,
      sponsorshipType,
      sponsorshipAbout,
      image,
      website,
    };

    return withLogging(
      'SocialHiveUpdateSponsor',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveRemoveSponsor(
    token: string,
    eventID: string,
    sponsorID: string
  ): Promise<AxiosResponse<RemoveSponsorResponse>> {
    const url = `${API_URL}flow/social-hive-remove-event-sponsor`;
    const requestData = { eventID, sponsorID };

    return withLogging(
      'SocialHiveRemoveSponsor',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveAddTicketToEvent(
    token: string,
    channelID: string,
    eventID: string,
    title: string,
    type: string,
    description: string,
    price: number,
    currency: string,
    trackInventory: boolean,
    inventory: number | null
  ): Promise<AxiosResponse<AddTicketToEventResponse>> {
    const url = `${API_URL}flow/social-hive-add-ticket-to-event`;
    const requestData = {
      channelID,
      eventID,
      title,
      type,
      description,
      price,
      currency,
      trackInventory,
      inventory,
    };

    return withLogging(
      'SocialHiveAddTicketToEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveUpdateTicketToEvent(
    token: string,
    channelID: string,
    eventID: string,
    ticketID: string,
    title: string,
    type: string,
    description: string,
    price: number,
    currency: string,
    trackInventory: boolean,
    inventory: number | null
  ): Promise<AxiosResponse<UpdateTicketToEventResponse>> {
    const url = `${API_URL}flow/social-hive-update-event-ticket`;
    const requestData = {
      channelID,
      eventID,
      ticketID,
      title,
      type,
      description,
      price,
      currency,
      trackInventory,
      inventory,
    };

    return withLogging(
      'SocialHiveUpdateTicketToEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveRemoveTicketFromEvent(
    token: string,
    eventID: string,
    ticketID: string
  ): Promise<AxiosResponse<RemoveTicketFromEventResponse>> {
    const url = `${API_URL}flow/social-hive-remove-event-ticket`;
    const requestData = { eventID, ticketID };

    return withLogging(
      'SocialHiveRemoveTicketFromEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveCreateSoldTicket(
    token: string,
    channelID: string,
    eventID: string,
    ticketID: string,
    paymentID: string,
    name: string,
    lastname: string,
    email: string,
    phone: string,
    isPaid: boolean,
    billing: Record<string, any>
  ): Promise<AxiosResponse<CreateSoldTicketResponse>> {
    const url = `${API_URL}flow/social-hive-create-sold-ticket`;
    const requestData = {
      channelID,
      eventID,
      ticketID,
      paymentID,
      name,
      lastname,
      email,
      phone,
      isPaid,
      billing,
    };

    return withLogging(
      'SocialHiveCreateSoldTicket',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveCommentOnEvent(
    token: string,
    channelID: string,
    eventID: string,
    content: string,
    repliedCommentID?: string | null
  ): Promise<AxiosResponse<CommentOnEventResponse>> {
    const url = `${API_URL}flow/social-hive-add-comment-on-channel-event`;
    const requestData = { channelID, eventID, content, repliedCommentID: repliedCommentID ?? null };

    return withLogging(
      'SocialHiveCommentOnEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveRemoveChannelEventComment(
    token: string,
    channelID: string,
    eventID: string,
    commentID: string
  ): Promise<AxiosResponse<RemoveChannelEventCommentResponse>> {
    const url = `${API_URL}flow/social-hive-remove-channel-event-comment`;
    const requestData: RemoveChannelEventCommentRequest = { channelID, eventID, commentID };

    return withLogging(
      'SocialHiveRemoveChannelEventComment',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveUseTicket(
    token: string,
    ticketNo: string
  ): Promise<AxiosResponse<UseTicketResponse>> {
    const url = `${API_URL}flow/social-hive-use-event-ticket`;
    const requestData = { ticketNo };

    return withLogging(
      'SocialHiveUseTicket',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  ExportToExcel(
    token: string,
    content: Array<{
      id: number;
      ticketNo: string;
      name: string;
      email: string;
      phone: string;
      type: string;
      amount: string;
      status: string;
      date: string;
    }>,
    header: {
      id: "number";
      ticketNo: "string";
      name: "string";
      email: "string";
      phone: "string";
      type: "string";
      amount: "string";
      status: "string";
      date: "string";
    },
    sheetTitle: string
  ): Promise<AxiosResponse<ExportToExcelResponse>> {
    const url = `${API_URL}flow/create-excel`;
    const requestData = {
      content,
      header,
      sheetTitle,
    };

    return withLogging(
      'ExportToExcel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  CreateEventVisit(
    token: string,
    channelID: string,
    eventID: string,
    userDetails: any
  ): Promise<AxiosResponse<CreateEventVisitResponse>> {
    const url = `${API_URL}flow/social-hive-create-analytics-channel-event-visit`;
    const requestData = { channelID, eventID, userDetails };

    return withLogging(
      'CreateEventVisit',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetMyChannels(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetMyChannelsResponse>> {
    const url = `${API_URL}flow/social-hive-get-my-channels`;

    return withLogging(
      'SocialHiveGetMyChannels',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SocialHiveSearchChannel(
    token: string,
    query: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<SearchChannelResponse>> {
    const url = `${API_URL}flow/social-hive-search-channel`;
    const requestData: SearchChannelRequest = { query };

    return withLogging(
      'SocialHiveSearchChannel',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveSearchEvent(
    token: string,
    requestData: SearchEventRequest,
    abortController?: AbortController
  ): Promise<AxiosResponse<SearchEventResponse>> {
    const url = `${API_URL}flow/social-hive-search-event`;

    return withLogging(
      'SocialHiveSearchEvent',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveSearchEventByCity(
    token: string,
    city: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<SearchEventResponse>> {
    const url = `${API_URL}flow/social-hive-search-event`;
    const requestData = { city };

    return withLogging(
      'SocialHiveSearchEventByCity',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveSearch(
    token: string,
    query: string,
    type?: 'channels' | 'events' | 'users',
    abortController?: AbortController
  ): Promise<AxiosResponse<UnifiedSearchResponse>> {
    const url = `${API_URL}flow/social-hive-search`;
    const requestData: UnifiedSearchRequest = { query };
    if (type) {
      requestData.type = type;
    }

    return withLogging(
      'SocialHiveSearch',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetChannel(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetChannelResponse>> {
    const url = `${API_URL}flow/social-hive-get-channel`;
    const requestData: GetChannelRequest = { channelID };

    return withLogging(
      'SocialHiveGetChannel',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveJoinToChannel(
    token: string,
    userID: string,
    channelID: string
  ): Promise<AxiosResponse<JoinToChannelResponse>> {
    const url = `${API_URL}flow/social-hive-join-to-channel`;
    const requestData = { userID, channelID };

    return withLogging(
      'SocialHiveJoinToChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveGetChannelUsers(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetChannelUsersResponse>> {
    const url = `${API_URL}flow/social-hive-get-users`;
    // Include status: [1] to get active channel members
    const requestData: GetChannelUsersRequest = { channelID, status: [1] };

    return withLogging(
      'SocialHiveGetChannelUsers',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetChannelUserRequests(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetChannelUserRequestsResponse>> {
    const url = `${API_URL}flow/social-hive-get-users`;
    // Include status: [3] to get users with pending status
    const requestData: GetChannelUserRequestsRequest = { channelID, status: [3] };

    return withLogging(
      'SocialHiveGetChannelUserRequests',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveAssignAdminToChannel(
    token: string,
    channelID: string,
    userID: string
  ): Promise<AxiosResponse<AssignAdminToChannelResponse>> {
    const url = `${API_URL}flow/social-hive-assign-user-as-admin-to-channel`;
    const requestData: AssignAdminToChannelRequest = { channelID, userID };

    return withLogging(
      'SocialHiveAssignAdminToChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveRevokeAdminFromChannel(
    token: string,
    channelID: string,
    userID: string
  ): Promise<AxiosResponse<RevokeAdminFromChannelResponse>> {
    const url = `${API_URL}flow/social-hive-revoke-user-as-admin-from-channel`;
    const requestData: RevokeAdminFromChannelRequest = { channelID, userID };

    return withLogging(
      'SocialHiveRevokeAdminFromChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveAcceptUserToPrivateChannel(
    token: string,
    channelID: string,
    userID: string
  ): Promise<AxiosResponse<AcceptUserToPrivateChannelResponse>> {
    const url = `${API_URL}flow/social-hive-private-channel-user-approve`;
    const requestData: AcceptUserToPrivateChannelRequest = { channelID, userID };

    return withLogging(
      'SocialHiveAcceptUserToPrivateChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveRejectUserToPrivateChannel(
    token: string,
    channelID: string,
    userID: string
  ): Promise<AxiosResponse<RejectUserToPrivateChannelResponse>> {
    const url = `${API_URL}flow/social-hive-private-channel-user-reject`;
    const requestData: RejectUserToPrivateChannelRequest = { channelID, userID };

    return withLogging(
      'SocialHiveRejectUserToPrivateChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveLeaveChannel(
    token: string,
    userID: string,
    channelID: string
  ): Promise<AxiosResponse<LeaveChannelResponse>> {
    const url = `${API_URL}flow/social-hive-leave-channel`;
    const requestData = { channelID, userID };

    return withLogging(
      'SocialHiveLeaveChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  GetSavedChannelEvents(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetSavedChannelEventsResponse>> {
    const url = `${API_URL}flow/social-hive-customer-get-saved-channel-events`;
    const requestData: any[] = [];

    return withLogging(
      'GetSavedChannelEvents',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SaveChannelEvent(
    token: string,
    channelID: string,
    eventID: string
  ): Promise<AxiosResponse<SaveChannelEventResponse>> {
    const url = `${API_URL}flow/social-hive-customer-save-channel-event`;
    const requestData = { channelID, eventID };

    return withLogging(
      'SaveChannelEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  RemoveSavedChannelEvent(
    token: string,
    channelID: string,
    eventID: string
  ): Promise<AxiosResponse<RemoveSavedChannelEventResponse>> {
    const url = `${API_URL}flow/social-hive-customer-remove-saved-channel-event`;
    const requestData = { channelID, eventID };

    return withLogging(
      'RemoveSavedChannelEvent',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  // Notification Services
  GetUserNotifications(
    token: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetUserNotificationsResponse>> {
    const url = `${API_URL}flow/user-notifications-get`;

    return withLogging(
      'GetUserNotifications',
      'GET',
      url,
      (signal) => axios({
        method: "get",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal
      }),
      undefined,
      abortController
    );
  },

  SetUserNotificationsRead(
    token: string,
    all: boolean,
    notificationID?: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<SetUserNotificationsReadResponse>> {
    const url = `${API_URL}flow/user-notifications-set-read`;
    const requestData: SetUserNotificationsReadRequest = all ? { all: true } : { notificationID };

    return withLogging(
      'SetUserNotificationsRead',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  // Payment Services
  InitializeSHPayment(
    requestData: InitializePaymentRequest
  ): Promise<AxiosResponse<InitializePaymentResponseType>> {
    const url = `${PAYMENT_API_URL}event-initialize-payment.php`;

    return withLogging(
      'InitializeSHPayment',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      { ...requestData, cardNumber: '[REDACTED]', cvc: '[REDACTED]' } // Don't log sensitive data
    );
  },

  ListenSHPayment(
    paymentID: string
  ): Promise<AxiosResponse<ListenPaymentResponseType>> {
    const url = `${PAYMENT_API_URL}event-listen.php`;
    const requestData: ListenPaymentRequest = { paymentID };

    return withLogging(
      'ListenSHPayment',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveCreateChannel(
    token: string,
    title: string,
    isPublic: boolean,
    description?: string,
    logo?: {
      file: string;
      extension: string;
      title: string;
    } | null,
    topBanner?: {
      file: string;
      extension: string;
      title: string;
    } | null
  ): Promise<AxiosResponse<CreateChannelResponse>> {
    const url = `${API_URL}flow/social-hive-create-channel`;
    const requestData: CreateChannelRequest = {
      title,
      description,
      isPublic,
      isFree: true,
      links: null,
      logo: logo ?? null,
      topBanner: topBanner ?? null,
      tags: null,
    };

    return withLogging(
      'SocialHiveCreateChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveUpdateChannel(
    token: string,
    channelID: string,
    title?: string,
    channelTitle?: string,
    description?: string,
    logo?: CoverFile | null,
    topBanner?: CoverFile | null,
    isPublic?: boolean,
    isFree?: boolean,
    links?: {
      instagram?: string;
      website?: string;
      x?: string;
      linkedin?: string;
      linkedinURL?: string;
    },
    iban?: string,
    bankAccountHolder?: string,
    bankAccountNo?: string
  ): Promise<AxiosResponse<UpdateChannelResponse>> {
    const url = `${API_URL}flow/social-hive-update-channel`;
    const requestData: UpdateChannelRequest = {
      channelID,
      title: title || channelTitle,
      channelTitle,
      description,
      logo,
      topBanner,
      isPublic,
      isFree,
      links,
      iban,
      bankAccountHolder,
      bankAccountNo,
    };

    return withLogging(
      'SocialHiveUpdateChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveDeleteChannel(
    token: string,
    channelID: string
  ): Promise<AxiosResponse<DeleteChannelResponse>> {
    const url = `${API_URL}flow/social-hive-remove-channel`;
    const requestData: DeleteChannelRequest = {
      channelID,
    };

    return withLogging(
      'SocialHiveDeleteChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
      }),
      requestData
    );
  },

  SocialHiveGetMyPaymentBalance(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetPaymentBalanceResponse>> {
    const url = `${API_URL}flow/social-hive-get-my-payment-balance`;
    const requestData = { channelID };

    return withLogging(
      'SocialHiveGetMyPaymentBalance',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveWithdrawBalance(
    token: string,
    paymentIDs: string[],
    abortController?: AbortController
  ): Promise<AxiosResponse<WithdrawBalanceResponse>> {
    const url = `${API_URL}flow/social-hive-withdraw-balance`;
    const requestData = { paymentIDs };

    return withLogging(
      'SocialHiveWithdrawBalance',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  CreateTogatherSession(
    token: string,
    requestData: CreateTogatherSessionRequest
  ): Promise<AxiosResponse<CreateTogatherSessionResponse>> {
    const url = `${API_URL}flow/analytics-create-togather-session`;

    return withLogging(
      'CreateTogatherSession',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SetUserOnlineInTogather(
    token: string,
    requestData: SetUserOnlineInTogatherRequest
  ): Promise<AxiosResponse<SetUserOnlineInTogatherResponse>> {
    const url = `${API_URL}flow/analytics-set-user-online-in-togather`;

    return withLogging(
      'SetUserOnlineInTogather',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  // --- User Profile & Social Methods ---

  GetAnyUserInfos(
    token: string,
    userID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetAnyUserInfosResponse>> {
    const url = `${API_URL}flow/get-any-user-infos`;
    const requestData: GetAnyUserInfosRequest = { userID };

    return withLogging(
      'GetAnyUserInfos',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveFollowUser(
    token: string,
    userID: string
  ): Promise<AxiosResponse<FollowUserResponse>> {
    const url = `${API_URL}flow/social-hive-follow-user`;
    const requestData: FollowUserRequest = { userID };

    return withLogging(
      'SocialHiveFollowUser',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveUnfollowUser(
    token: string,
    userID: string
  ): Promise<AxiosResponse<UnfollowUserResponse>> {
    const url = `${API_URL}flow/social-hive-unfollow-user`;
    const requestData: UnfollowUserRequest = { userID };

    return withLogging(
      'SocialHiveUnfollowUser',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveRejectFollower(
    token: string,
    userID: string
  ): Promise<AxiosResponse<RejectFollowerResponse>> {
    const url = `${API_URL}flow/social-hive-reject-follower-user`;
    const requestData: RejectFollowerRequest = { userID };

    return withLogging(
      'SocialHiveRejectFollower',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveAmIFollowingUser(
    token: string,
    userID: string
  ): Promise<AxiosResponse<AmIFollowingUserResponse>> {
    const url = `${API_URL}flow/social-hive-am-i-following-user`;
    const requestData: AmIFollowingUserRequest = { userID };

    return withLogging(
      'SocialHiveAmIFollowingUser',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetMyFollowers(
    token: string,
    userID?: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetMyFollowersResponse>> {
    const url = `${API_URL}flow/social-hive-get-my-followers`;
    const requestData: any = userID ? { userID } : {};

    return withLogging(
      'SocialHiveGetMyFollowers',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  SocialHiveGetMyFollowings(
    token: string,
    userID?: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<GetMyFollowingsResponse>> {
    const url = `${API_URL}flow/social-hive-get-my-followings`;
    const requestData: any = userID ? { userID } : {};

    return withLogging(
      'SocialHiveGetMyFollowings',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  UpdateUserData(
    token: string,
    userID: string,
    updateData: Record<string, unknown>,
    abortController?: AbortController
  ): Promise<AxiosResponse<UpdateUserDataResponse>> {
    const url = `${API_URL}flow/update-user`;
    const requestData: UpdateUserDataRequest = { id: userID, updateData };

    return withLogging(
      'UpdateUserData',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  // ============================================
  // Segmentation / Tags APIs
  // ============================================

  /**
   * Get all segmentations (tags) for a channel
   */
  SocialHiveGetChannelSegmentations(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<{ status: boolean; result?: string[]; error?: string }>> {
    const url = `${API_URL}flow/social-hive-get-channel-segmentations`;
    const requestData = { channelID };

    return withLogging(
      'SocialHiveGetChannelSegmentations',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  /**
   * Get channel segmentations with user count for each tag
   */
  SocialHiveGetChannelSegmentationsWithUserCount(
    token: string,
    channelID: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<{ status: boolean; result?: { tag: string; userCount: string }[]; error?: string }>> {
    const url = `${API_URL}flow/social-hive-get-channel-segmentations-with-user-count`;
    const requestData = { channelID };

    return withLogging(
      'SocialHiveGetChannelSegmentationsWithUserCount',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  /**
   * Add a new segmentation (tag) to a channel
   */
  SocialHiveAddSegmentationToChannel(
    token: string,
    channelID: string,
    tag: string
  ): Promise<AxiosResponse<{ status: boolean; message?: string; error?: string }>> {
    const url = `${API_URL}flow/social-hive-add-segmentation-to-channel`;
    const requestData = { channelID, tag };

    return withLogging(
      'SocialHiveAddSegmentationToChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  /**
   * Remove a segmentation (tag) from a channel
   */
  SocialHiveRemoveSegmentationFromChannel(
    token: string,
    channelID: string,
    tag: string
  ): Promise<AxiosResponse<{ status: boolean; message?: string; error?: string }>> {
    const url = `${API_URL}flow/social-hive-remove-segmentation-to-channel`;
    const requestData = { channelID, tag };

    return withLogging(
      'SocialHiveRemoveSegmentationFromChannel',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  /**
   * Add a channel tag to multiple users
   */
  SocialHiveAddChannelTagToUsers(
    token: string,
    channelID: string,
    tag: string,
    userList: string[]
  ): Promise<AxiosResponse<{ status: boolean; message?: string; error?: string }>> {
    const url = `${API_URL}flow/social-hive-add-channel-tag-to-channel-users`;
    const requestData = { channelID, tag, userList };

    return withLogging(
      'SocialHiveAddChannelTagToUsers',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  /**
   * Remove a channel tag from multiple users
   */
  SocialHiveRemoveChannelTagFromUsers(
    token: string,
    channelID: string,
    tag: string,
    userList: string[]
  ): Promise<AxiosResponse<{ status: boolean; message?: string; error?: string }>> {
    const url = `${API_URL}flow/social-hive-remove-channel-tag-from-channel-users`;
    const requestData = { channelID, tag, userList };

    return withLogging(
      'SocialHiveRemoveChannelTagFromUsers',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  /**
   * Get users by segment (tag) for a channel
   */
  /**
   * Rename a segmentation (tag) on a channel
   */
  SocialHiveUpdateChannelSegment(
    token: string,
    channelID: string,
    tag: string,
    tagReplaceWith: string
  ): Promise<AxiosResponse<{ status: boolean; message?: string; error?: string }>> {
    const url = `${API_URL}flow/social-hive-update-channel-segment`;
    const requestData = { channelID, tag, tagReplaceWith };

    return withLogging(
      'SocialHiveUpdateChannelSegment',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetUsersBySegment(
    token: string,
    channelID: string,
    tag: string,
    abortController?: AbortController
  ): Promise<AxiosResponse<{ status: boolean; result?: ChannelUser[]; error?: string }>> {
    const url = `${API_URL}flow/social-hive-get-users-by-segment`;
    const requestData = { channelID, tag };

    return withLogging(
      'SocialHiveGetUsersBySegment',
      'POST',
      url,
      (signal) => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData,
        signal
      }),
      requestData,
      abortController
    );
  },

  // Announcement Services
  SocialHiveCreateAnnouncement(
    token: string,
    channelID: string,
    title: string,
    description: string,
    type: string,
    subTitle: string,
    image: { file: string; extension: string } | null,
    status: number
  ): Promise<AxiosResponse<{ status: boolean; result?: any; error?: string }>> {
    const url = `${API_URL}flow/social-hive-create-announcement`;
    const requestData: any = { channelID, title, description, type, subTitle, status };
    if (image) requestData.image = image;

    return withLogging(
      'SocialHiveCreateAnnouncement',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveUpdateAnnouncement(
    token: string,
    announcementID: string,
    title: string,
    description: string,
    type: string,
    subTitle: string,
    image: { file: string; extension: string } | null,
    status: number
  ): Promise<AxiosResponse<{ status: boolean; result?: any; error?: string }>> {
    const url = `${API_URL}flow/social-hive-update-announcement`;
    const requestData: any = { announcementID, title, description, type, subTitle, status };
    if (image) requestData.image = image;

    return withLogging(
      'SocialHiveUpdateAnnouncement',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveRemoveAnnouncement(
    token: string,
    announcementID: string
  ): Promise<AxiosResponse<{ status: boolean; result?: any; error?: string }>> {
    const url = `${API_URL}flow/social-hive-remove-announcement`;
    const requestData = { announcementID };

    return withLogging(
      'SocialHiveRemoveAnnouncement',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetAnnouncements(
    token: string,
    channelID: string
  ): Promise<AxiosResponse<{ status: boolean; result?: any[]; error?: string }>> {
    const url = `${API_URL}flow/social-hive-get-announcements`;
    const requestData = { channelID };

    return withLogging(
      'SocialHiveGetAnnouncements',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  },

  SocialHiveGetAnnouncement(
    token: string,
    channelID: string,
    announcementID: string
  ): Promise<AxiosResponse<{ status: boolean; result?: any; error?: string }>> {
    const url = `${API_URL}flow/social-hive-get-announcement`;
    const requestData = { channelID, announcementID };

    return withLogging(
      'SocialHiveGetAnnouncement',
      'POST',
      url,
      () => axios({
        method: "post",
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: requestData
      }),
      requestData
    );
  }
};

export default Services;
